import { map } from 'lodash';
import { epoch, utc } from '../../util/datetime';
import { toUUID } from '../../util/transform';
import Address from '../address/Address';
import { ContactMaritalStatusOption } from './ContactMaritalStatusOption.enum';
import { ContactDriverLicenceType } from './ContactDriverLicenceType.enum';
import { ContactGender } from './ContactGender.enum';
import { ContactMedicareCardColor } from './ContactMedicareCardColor.enum';
import { ContactNextOfKinRelationship } from './ContactNextOfKinRelationship.enum';
import { ContactPersonalReferenceRelationship } from './ContactPersonalReferenceRelationship.enum';
import { ContactResidencyStatusOption } from './ContactResidencyStatusOption.enum';
import { ContactSourceOfWealth } from './ContactSourceOfWealth.enum';
import { PersonalTitle } from '../global/PersonalTitle.enum';
import ContactPersonDependent from './ContactPersonDependent';
import type { IContactPersonInformation } from './ContactPersonInformation.interface';

export default class ContactPersonInformation implements IContactPersonInformation {
  title: PersonalTitle;
  firstName: string;
  middleName: string;
  familyName: string;
  preferredName: string;
  previousName: string;
  dateOfBirth: number;
  gender: ContactGender;
  countryOfResidency: string;
  countryOfTaxResidence: string;
  citizenship: string;
  residentialStatus: ContactResidencyStatusOption;
  countryOfBirth: string;
  cityOfBirth: string;
  driversLicenseNumber: string;
  driversLicenseCardNumber: string;
  driversLicenseVersionNumber: number;
  driversLicenseNameOnDocument: string;
  driversLicenseType: number;
  driversLicenseStateOfIssue: string;
  driversLicenseExpiryDate: number;
  passportNumber: string;
  passportNameOnDocument: string;
  passportIssueCountry: string;
  passportIssueDate: number;
  passportExpiryDate: number;
  medicareNumber: string;
  medicareReferenceNumber: string;
  medicareNameOnCard: string;
  medicareCardColor: ContactMedicareCardColor;
  medicareExpiryDate: number;
  maritalStatus: ContactMaritalStatusOption;
  numberOfDependents: number;
  dependents: Array<ContactPersonDependent>;
  mothersMaidenName: string;
  useExistingContactAsSpouse: string;
  idSpouse: string;
  spouseFirstName: string;
  spouseFamilyName: string;
  nextOfKinFullName: string;
  nextOfKinAddress: Address;
  nextOfKinRelationship: string;
  nextOfKinPhoneCode: string;
  nextOfKinPhone: string;
  personalReferenceFullName1: string;
  personalReferenceAddress1: Address;
  personalReferenceRelationship1: ContactPersonalReferenceRelationship;
  personalReferencePhoneCode1: string;
  personalReferencePhone1: string;
  personalReferenceFullName2: string;
  personalReferenceAddress2: Address;
  personalReferenceRelationship2: ContactPersonalReferenceRelationship;
  personalReferencePhoneCode2: string;
  personalReferencePhone2: string;
  sourceOfWealth: ContactSourceOfWealth;
  sourceOfFunds: ContactSourceOfWealth;

  static build = (data: IContactPersonInformation) => new ContactPersonInformation(data);
  static blank = () => new ContactPersonInformation();

  /**
   * Constructor
   * @param {ContactPersonInformation} data
   */
  constructor(data?: IContactPersonInformation) {
    this.title = PersonalTitle.of(data?.title?.name || data?.title?.toString());
    this.firstName = data?.firstName || '';
    this.middleName = data?.middleName || '';
    this.familyName = data?.familyName || '';
    this.preferredName = data?.preferredName || this.firstName;
    this.previousName = data?.previousName || '';
    this.dateOfBirth = data?.dateOfBirth ? epoch(utc(data?.dateOfBirth)) : 0;
    this.gender = ContactGender.of(data?.gender?.name);
    this.countryOfResidency = data?.countryOfResidency || undefined;
    this.countryOfTaxResidence = data?.countryOfTaxResidence || undefined;
    this.citizenship = data?.citizenship || undefined;
    this.residentialStatus = ContactResidencyStatusOption.of(
      data?.residentialStatus?.name || data?.residentialStatus?.toString()
    );
    this.countryOfBirth = data?.countryOfBirth || undefined;
    this.cityOfBirth = data?.cityOfBirth || '';
    this.driversLicenseNumber = data?.driversLicenseNumber || '';
    this.driversLicenseCardNumber = data?.driversLicenseCardNumber || '';
    this.driversLicenseVersionNumber = parseInt(data?.driversLicenseVersionNumber || 0, 10);
    this.driversLicenseNameOnDocument = data?.driversLicenseNameOnDocument || '';
    this.driversLicenseType = ContactDriverLicenceType.of(
      data?.driversLicenseType?.name || data?.driversLicenseType?.toString()
    );
    this.driversLicenseStateOfIssue = data?.driversLicenseStateOfIssue || '';
    this.driversLicenseExpiryDate = data?.driversLicenseExpiryDate
      ? epoch(utc(data?.driversLicenseExpiryDate))
      : 0;
    this.passportNumber = data?.passportNumber || '';
    this.passportNameOnDocument = data?.passportNameOnDocument || '';
    this.passportIssueCountry = data?.passportIssueCountry || undefined;
    this.passportIssueDate = data?.passportIssueDate ? epoch(utc(data?.passportIssueDate)) : 0;
    this.passportExpiryDate = data?.passportExpiryDate
      ? epoch(utc(data?.passportExpiryDate))
      : 0;
    this.medicareNumber = data?.medicareNumber || '';
    this.medicareReferenceNumber = data?.medicareReferenceNumber || '';
    this.medicareNameOnCard = data?.medicareNameOnCard || '';
    this.medicareCardColor = ContactMedicareCardColor.of(
      data?.medicareCardColor?.name || data?.medicareCardColor?.toString()
    );
    this.medicareExpiryDate = data?.medicareExpiryDate
      ? epoch(utc(data?.medicareExpiryDate))
      : 0;
    this.maritalStatus = ContactMaritalStatusOption.of(
      data?.maritalStatus?.name || data?.maritalStatus?.toString()
    );
    this.numberOfDependents = parseInt(data?.numberOfDependents || 0, 10);
    this.dependents = map(data?.dependents, (obj) => new ContactPersonDependent(obj));
    this.mothersMaidenName = data?.mothersMaidenName || '';
    this.useExistingContactAsSpouse =
      toUUID(data?.useExistingContactAsSpouse) || data?.useExistingContactAsSpouse || undefined;
    this.idSpouse = toUUID(data?.idSpouse);
    this.spouseFirstName = data?.spouseFirstName || '';
    this.spouseFamilyName = data?.spouseFamilyName || '';
    this.nextOfKinFullName = data?.nextOfKinFullName || '';
    this.nextOfKinAddress = new Address(data?.nextOfKinAddress);
    this.nextOfKinRelationship = ContactNextOfKinRelationship.of(
      data?.nextOfKinRelationship?.name || data?.nextOfKinRelationship?.toString()
    );
    this.nextOfKinPhoneCode = data?.nextOfKinPhoneCode || '';
    this.nextOfKinPhone = data?.nextOfKinPhone || '';
    this.personalReferenceFullName1 = data?.personalReferenceFullName1 || '';
    this.personalReferenceAddress1 = new Address(data?.personalReferenceAddress1);
    this.personalReferenceRelationship1 = ContactPersonalReferenceRelationship.of(
      data?.personalReferenceRelationship1?.name ||
        data?.personalReferenceRelationship1?.toString()
    );
    this.personalReferencePhoneCode1 = data?.personalReferencePhoneCode1 || '';
    this.personalReferencePhone1 = data?.personalReferencePhone1 || '';
    this.personalReferenceFullName2 = data?.personalReferenceFullName2 || '';
    this.personalReferenceAddress2 = new Address(data?.personalReferenceAddress2);
    this.personalReferenceRelationship2 = ContactPersonalReferenceRelationship.of(
      data?.personalReferenceRelationship2?.name ||
        data?.personalReferenceRelationship2?.toString()
    );
    this.personalReferencePhoneCode2 = data?.personalReferencePhoneCode2 || '';
    this.personalReferencePhone2 = data?.personalReferencePhone2 || '';
    this.sourceOfWealth = ContactSourceOfWealth.of(
      data?.sourceOfWealth?.name || data?.sourceOfWealth?.toString()
    );
    this.sourceOfFunds = ContactSourceOfWealth.of(
      data?.sourceOfFunds?.name || data?.sourceOfFunds?.toString()
    );
  }
}
